import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { API_BASE_URL, buildApiUrl } from '../services/api';

export type OfficialIdentificationState = {
  hasDocument: boolean;
  documentUrl: string | null;
  isImage: boolean;
};

type OfficialIdentificationCardProps = {
  title?: string;
  description?: string;
  className?: string;
  onChange?: (state: OfficialIdentificationState) => void;
};

export function OfficialIdentificationCard({
  title = 'Identificacao do Oficial de Justica',
  description = 'Envie a imagem da sua identificacao para uso exclusivo nas acoes de envio. Esse arquivo nao se mistura com o documento do cadastro.',
  className = '',
  onChange
}: OfficialIdentificationCardProps) {
  const identificationInputRef = useRef<HTMLInputElement | null>(null);
  const [loadingIdentification, setLoadingIdentification] = useState(false);
  const [uploadingIdentification, setUploadingIdentification] = useState(false);
  const [identificationUrl, setIdentificationUrl] = useState<string | null>(null);

  const emitChange = useCallback((state: OfficialIdentificationState) => {
    onChange?.(state);
  }, [onChange]);

  const authenticatedFetch = useCallback(async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('auth_token');
    const selectedTenantId = localStorage.getItem('superadmin_selected_tenant') || localStorage.getItem('selected_tenant_id');
    const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
    const headers: HeadersInit = {
      ...options.headers,
    };

    if (!isFormData && !(headers as Record<string, string>)['Content-Type']) {
      (headers as Record<string, string>)['Content-Type'] = 'application/json';
    }

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    if (selectedTenantId) {
      (headers as Record<string, string>)['X-Tenant-Id'] = selectedTenantId;
    }

    const resolvedUrl =
      /^https?:\/\//i.test(url) || url.startsWith(API_BASE_URL)
        ? url
        : buildApiUrl(url);

    return fetch(resolvedUrl, {
      ...options,
      headers,
    });
  }, []);

  const loadOfficialIdentification = useCallback(async () => {
    try {
      setLoadingIdentification(true);
      const resp = await authenticatedFetch('/auth/oficial-identificacao');
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

      const data = await resp.json();
      const nextState = {
        hasDocument: Boolean(data?.data?.documentUrl),
        documentUrl: data?.data?.documentUrl || null,
        isImage: Boolean(data?.data?.isImage)
      };

      setIdentificationUrl(nextState.documentUrl);
      emitChange(nextState);
    } catch (error) {
      console.error('Erro ao carregar identificacao do oficial:', error);
      toast.error('Nao foi possivel carregar a imagem de identificacao');
    } finally {
      setLoadingIdentification(false);
    }
  }, [authenticatedFetch, emitChange]);

  useEffect(() => {
    loadOfficialIdentification();
  }, [loadOfficialIdentification]);

  const handleIdentificationUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploadingIdentification(true);
      const formData = new FormData();
      formData.append('documentoComprovante', file);

      const resp = await authenticatedFetch('/auth/oficial-identificacao', {
        method: 'POST',
        body: formData,
      });

      const data = await resp.json().catch(() => null);
      if (!resp.ok) {
        throw new Error(data?.message || `HTTP ${resp.status}`);
      }

      const nextState = {
        hasDocument: Boolean(data?.data?.documentUrl),
        documentUrl: data?.data?.documentUrl || null,
        isImage: Boolean(data?.data?.isImage)
      };

      setIdentificationUrl(nextState.documentUrl);
      emitChange(nextState);
      toast.success('Imagem de identificacao enviada com sucesso');
    } catch (error: any) {
      console.error('Erro ao enviar identificacao do oficial:', error);
      toast.error(error?.message || 'Erro ao enviar imagem de identificacao');
    } finally {
      setUploadingIdentification(false);
      event.target.value = '';
    }
  };

  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-100 p-3 ${className}`.trim()}>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
          <p className="text-xs text-gray-500 mt-1 leading-5">{description}</p>
        </div>
        <div className="w-full md:w-auto md:max-w-[420px] rounded-lg border border-dashed border-blue-200 bg-blue-50/60 p-2.5 flex flex-wrap items-center gap-1.5">
          <input
            ref={identificationInputRef}
            type="file"
            className="hidden"
            accept="image/png,image/jpeg,image/webp"
            onChange={handleIdentificationUpload}
          />
          <button
            type="button"
            onClick={() => identificationInputRef.current?.click()}
            disabled={uploadingIdentification}
            className="px-3 py-1.5 text-xs rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {uploadingIdentification
              ? 'Enviando...'
              : (identificationUrl ? 'Alterar imagem' : 'Enviar identificacao do oficial')}
          </button>
          {loadingIdentification ? (
            <span className="text-xs text-gray-500">Carregando imagem...</span>
          ) : identificationUrl ? (
            <a
              href={identificationUrl}
              target="_blank"
              rel="noreferrer"
              className="text-xs font-medium text-blue-700 hover:text-blue-900"
            >
              Visualizar minha identificacao
            </a>
          ) : (
            <span className="text-xs text-gray-500">Nenhuma imagem enviada</span>
          )}
        </div>
      </div>
    </div>
  );
}
